import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { fetchCurrentTenantGitProvider } from "../../../APIs/PrManagementAPI";
import {
  createGitHubConfigurationMigrationPullRequests,
  fetchGitHubConfigurationMigrationRepositories,
  previewGitHubConfigurationMigration,
} from "../../../APIs/githubConfigurationMigrationAPI";

const MAX_REPOSITORIES = 10;

const errorMessage = (error) => {
  const data = error?.response?.data;
  return data?.message || data?.detail || error?.message || "The request failed.";
};

function GitHubConfigurationMigration() {
  const [repositories, setRepositories] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetchCurrentTenantGitProvider()
      .then((provider) => {
        if (!active) return null;
        if (provider !== "GITHUB") {
          setPageStatus("unsupported");
          return null;
        }
        return fetchGitHubConfigurationMigrationRepositories();
      })
      .then((data) => {
        if (!active || data === null) return;
        setRepositories(Array.isArray(data) ? data : []);
        setPageStatus("ready");
      })
      .catch((requestError) => {
        if (active) {
          setError(errorMessage(requestError));
          setPageStatus("error");
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const visibleRepositories = useMemo(() => {
    const query = search.trim().toLowerCase();
    return repositories.filter((repository) => (
      String(repository.name || "").toLowerCase().includes(query)
    ));
  }, [repositories, search]);

  const allPreviewedRepositoriesReady = Boolean(
    preview?.repositories?.length
      && preview.repositories.length === selectedNames.length
      && preview.repositories.every((repository) => (
        (!repository.blockingIssues || repository.blockingIssues.length === 0)
        && repository.branches?.length === 1
        && repository.branches.every((branch) => branch.status === "PREVIEW_READY")
      ))
  );

  const toggleRepository = (name, checked) => {
    setSelectedNames((current) => {
      if (!checked) return current.filter((item) => item !== name);
      if (current.length >= MAX_REPOSITORIES) return current;
      return [...current, name];
    });
    setPreview(null);
    setResults(null);
    setError("");
  };

  const clearSelection = () => {
    setSelectedNames([]);
    setPreview(null);
    setResults(null);
    setError("");
  };

  const runPreview = async () => {
    setReviewOpen(false);
    setBusy(true);
    setError("");
    setResults(null);
    try {
      setPreview(await previewGitHubConfigurationMigration(selectedNames));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const createPullRequests = async () => {
    setBusy(true);
    setError("");
    try {
      setResults(await createGitHubConfigurationMigrationPullRequests(selectedNames));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const displayedResults = results?.repositories || preview?.repositories || [];

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Azure Pipelines to GitHub Actions
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Preview the generated GitHub Actions and repository updates, then create separate
        a configuration pull request against develop only. Deployable services with
        build and deployment modules receive an Auto-deploy workflow for pushes to main,
        develop, release/*, and hotfix/*; Maven artifact libraries keep their separate
        publishing workflow.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {pageStatus === "loading" && <CircularProgress size={24} />}
      {pageStatus === "unsupported" && (
        <Alert severity="warning">
          Azure Pipelines to GitHub Actions is available only when a GitHub tenant is selected.
          Switch to a GitHub tenant to continue.
        </Alert>
      )}
      {pageStatus === "ready" && (
        <>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {`GitHub repositories retrieved: ${repositories.length}`}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Select GitHub repositories ({selectedNames.length}/{MAX_REPOSITORIES})
        </Typography>
        <TextField
          label="Search repositories"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 1 }}
        />
        <List dense sx={{ maxHeight: 320, overflow: "auto" }}>
          {visibleRepositories.map((repository) => (
            <ListItem key={repository.id || repository.name} disablePadding>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selectedNames.includes(repository.name)}
                    disabled={!selectedNames.includes(repository.name) && selectedNames.length >= MAX_REPOSITORIES}
                    onChange={(event) => toggleRepository(repository.name, event.target.checked)}
                  />
                )}
                label={repository.name}
              />
            </ListItem>
          ))}
          {visibleRepositories.length === 0 && (
            <ListItem><ListItemText primary="No repositories found." /></ListItem>
          )}
        </List>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={clearSelection}
            disabled={busy || selectedNames.length === 0}
          >
            Unselect all
          </Button>
          <Button
            variant="contained"
            onClick={() => setReviewOpen(true)}
            disabled={busy || selectedNames.length === 0}
          >
            Review selected repositories
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Review selected repositories</DialogTitle>
        <DialogContent dividers>
          <Typography color="text.secondary" sx={{ mb: 1 }}>
            Preview changes will be generated for these repositories. No pull requests
            are created until you review the generated changes and confirm.
          </Typography>
          <List dense>
            {selectedNames.map((name) => (
              <ListItem key={name}>
                <ListItemText primary={name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Back to selection</Button>
          <Button variant="contained" onClick={runPreview} disabled={busy}>
            Preview changes
          </Button>
        </DialogActions>
      </Dialog>

      {displayedResults.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            {results ? "Pull request results" : "Migration preview"}
          </Typography>
          {displayedResults.map((repository) => (
            <Paper key={repository.repository} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {repository.repository}
              </Typography>
              {repository.blockingIssues?.map((issue) => (
                <Alert key={issue} severity="error" sx={{ mt: 1 }}>{issue}</Alert>
              ))}
              {repository.warnings?.map((warning) => (
                <Alert key={warning} severity="warning" sx={{ mt: 1 }}>{warning}</Alert>
              ))}
              {repository.branches?.map((branch) => (
                <Box key={branch.baseBranch} sx={{ mt: 2, pl: 1 }}>
                  <Typography variant="subtitle2">
                    {branch.featureBranch} → {branch.baseBranch}: {branch.status}
                  </Typography>
                  {branch.status === "PULL_REQUEST_ALREADY_EXISTS" && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      Pull request already exists for this migration branch. Review the existing PR; it will not be recreated.
                    </Alert>
                  )}
                  {branch.status === "SKIPPED_EXISTING_BRANCH" && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      The migration branch already exists without a matching open PR. It was not overwritten.
                    </Alert>
                  )}
                  {branch.message && <Typography variant="body2">{branch.message}</Typography>}
                  {branch.pullRequestUrl && (
                    <Link href={branch.pullRequestUrl} target="_blank" rel="noreferrer">
                      Open pull request
                    </Link>
                  )}
                  {branch.changedFiles?.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Files: {branch.changedFiles.join(", ")}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          ))}
          {!results && (
            <Button
              variant="contained"
              color="success"
              onClick={createPullRequests}
              disabled={busy || !allPreviewedRepositoriesReady}
            >
              Create pull requests
            </Button>
          )}
        </Box>
      )}
        </>
      )}
    </Box>
  );
}

export default GitHubConfigurationMigration;
